import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import useInView from '../../utils/useInView';

interface CardProps {
  image: string;
  title: string;
  subtitle?: string;
  description?: string;
  link?: string;
  tags?: string[];
  price?: string;
  hoverEffect?: boolean;
  onClick?: () => void;
  delay?: number;
  animate?: boolean;
  children?: ReactNode;
}

const CardContainer = styled(motion.div)<{ $hoverEffect: boolean }>`
  position: relative;
  width: 100%;
  background-color: white;
  border: 1px solid #eaeaea;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.4s ease;
  border-radius: 6px;
  
  ${({ $hoverEffect }) => $hoverEffect && `
    &:hover {
      transform: translateY(-10px);
      box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
      
      .card-image {
        transform: scale(1.05);
      }
      
      .card-overlay {
        opacity: 0.4;
      }
    }
  `}
`;

const CardImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 280px;
  overflow: hidden;
`;

const CardImage = styled.div<{ $image: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${({ $image }) => $image});
  background-size: cover;
  background-position: center;
  transition: transform 0.7s ease;
  transform-origin: center;
`;

const CardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.7));
  opacity: 0.6;
  transition: opacity 0.4s ease;
`;

const CardContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: white;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  line-height: 1.3;
  color: #333;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardSubtitle = styled.div`
  color: #666;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardDescription = styled.p`
  color: #555;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  line-height: 1.6;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const CardTag = styled.span`
  background-color: rgba(0, 0, 0, 0.1);
  color: #555;
  padding: 0.3rem 0.8rem;
  font-size: 0.7rem;
  border-radius: 0;
  text-transform: uppercase;
  letter-spacing: 0.05rem;
`;

const CardPrice = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  margin-top: auto;
  color: #004744;
`;

const CardLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
  height: 100%;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }
`;

const Card: React.FC<CardProps> = ({
  image,
  title,
  subtitle,
  description,
  link,
  tags,
  price,
  hoverEffect = true,
  onClick,
  delay = 0,
  animate = true,
  children
}) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
    rootMargin: '-50px 0px'
  });
  
  const cardContent = (
    <>
      <CardImageWrapper>
        <CardImage $image={image} className="card-image" />
        <CardOverlay className="card-overlay" />
      </CardImageWrapper>
      <CardContent>
        <CardTitle>{title}</CardTitle>
        {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}
        
        {tags && tags.length > 0 && (
          <CardTags>
            {tags.map((tag, index) => (
              <CardTag key={index}>{tag}</CardTag>
            ))}
          </CardTags>
        )}
        
        {description && <CardDescription>{description}</CardDescription>}
        {price && <CardPrice>{price}</CardPrice>}
        {children}
      </CardContent>
    </>
  );

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const card = (
    <CardContainer
      ref={ref as React.RefObject<HTMLDivElement>}
      $hoverEffect={hoverEffect}
      initial={animate ? { opacity: 0, y: 30 } : { opacity: 1, y: 0 }}
      animate={animate && inView ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: 0.6, delay: delay, ease: "easeInOut" }}
      onClick={onClick ? handleClick : undefined}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {link ? <CardLink to={link}>{cardContent}</CardLink> : cardContent}
    </CardContainer>
  );

  return card;
};

export default Card; 